'use client'

import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'

type PlatformSubnavItem = {
  href: `#${string}`
  label: string
}

function getSectionId(href: string) {
  return href.replace('#', '')
}

export function PlatformSubnav({ items }: { items: readonly PlatformSubnavItem[] }) {
  const [activeSection, setActiveSection] = useState<string>(() => getSectionId(items[0]?.href ?? ''))

  const sectionIds = useMemo(() => items.map((item) => getSectionId(item.href)), [items])

  const scrollLockRef = useRef<{ targetId: string } | null>(null)
  const scrollLockTimeoutRef = useRef<number | null>(null)
  const rafIdRef = useRef<number | null>(null)

  const scrollToSection = (sectionId: string, href: `#${string}`) => {
    const el = document.getElementById(sectionId)
    if (!el) return

    window.history.pushState(null, '', href)
    setActiveSection(sectionId)

    scrollLockRef.current = { targetId: sectionId }
    if (scrollLockTimeoutRef.current != null) {
      window.clearTimeout(scrollLockTimeoutRef.current)
      scrollLockTimeoutRef.current = null
    }
    scrollLockTimeoutRef.current = window.setTimeout(() => {
      scrollLockRef.current = null
      scrollLockTimeoutRef.current = null
    }, 1200)

    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    const computeCurrentSection = () => {
      let current = sectionIds[0] ?? ''

      for (const id of sectionIds) {
        const el = document.getElementById(id)
        if (!el) continue
        const top = el.getBoundingClientRect().top
        if (top <= 220) current = id
      }

      setActiveSection(current)
    }

    const onScroll = () => {
      const lock = scrollLockRef.current
      if (lock) {
        const targetEl = document.getElementById(lock.targetId)
        const nearTarget =
          targetEl && Math.abs(targetEl.getBoundingClientRect().top) <= 8

        if (nearTarget) {
          scrollLockRef.current = null
          if (scrollLockTimeoutRef.current != null) {
            window.clearTimeout(scrollLockTimeoutRef.current)
            scrollLockTimeoutRef.current = null
          }
        } else {
          return
        }
      }

      if (rafIdRef.current != null) return
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null
        computeCurrentSection()
      })
    }

    computeCurrentSection()
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafIdRef.current != null) {
        window.cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
      if (scrollLockTimeoutRef.current != null) {
        window.clearTimeout(scrollLockTimeoutRef.current)
        scrollLockTimeoutRef.current = null
      }
    }
  }, [sectionIds])

  return (
    <div className="flex h-14 items-center justify-start space-x-12 font-mono text-fs-md font-bold uppercase tracking-widest">
      {items.map((item) => {
        const sectionId = getSectionId(item.href)
        const isActive = activeSection === sectionId

        return (
          <Link
            key={item.href}
            href={item.href}
            onClick={(e) => {
              e.preventDefault()
              scrollToSection(sectionId, item.href)
            }}
            className={
              isActive
                ? 'subnav-link flex h-full items-center border-b-2 border-brand-red text-brand-red'
                : 'subnav-link flex h-full items-center border-b-2 border-transparent text-charcoal-400 transition-colors hover:text-white'
            }
          >
            {item.label}
          </Link>
        )
      })}
    </div>
  )
}
